import { EnrollmentGroups } from "@/store/modules"
import { processGroupData, formatSupplySheds } from "@/utility/contractGroups"
import { deepClone } from "@/utility"
import {
  getSupplySheds,
  listOverview,
  submitSupplyRequests,
  applyFssStatusChangePending,
  submitEvidencingLink,
  submitConfirmRequests,
  fetchClientProfiles,
  fetchGroupClientProfiles,
  addProfileToGroup,
  removeProfileFromGroup,
  swapGroupProfile,
  updateProfile,
  createProfile,
  listFieldsForEntityManagement,
  updateLegalEntityMetadata,
  removeFieldsFromLegalEntity,
  addFieldsToLegalEntity,
  createNewLegalEntity,
  deleteLegalEntity
} from "@/api/ContractGroupAPI"
import {
  CP_VIEW,
  ADD_PROFILE,
  REMOVE_PROFILE,
  UPDATE_PROFILE,
  SWAP_PROFILE,
  CREATE_PROFILE,
  SUPPLY_SHED_TABLE,
  FSS_INDIVIUAL,
  ENROLLMENT_TABLE,
  CP_FSS_TABLE,
  CLIENT_PROFILES,
} from "@/constants/contractGroups"
import CarbonReadyAPI from "@/api/CarbonReadyAPI"

const tableLoadingState = {}
tableLoadingState[CLIENT_PROFILES] = { loading: true, error: null }
tableLoadingState[ENROLLMENT_TABLE] = { loading: true, error: null }
tableLoadingState[SUPPLY_SHED_TABLE] = { loading: true, error: null }
tableLoadingState[CP_FSS_TABLE] = { loading: true, error: null }
tableLoadingState[FSS_INDIVIUAL] = []

const state = {
  ...tableLoadingState,
  allClientProfiles: null,
  view: CP_VIEW,
  groupData: {},
  supplyShedData: [],
  channelPartnerWideFSS: null,
  geoIDLUT: [],
  filters: {},
}

const getters = {
  [EnrollmentGroups.Getters.stateGeoIDLUT](state) {
    return state.geoIDLUT.reduce((accum, curr) => {
      if (!accum.hasOwnProperty(curr['state_name'])) accum[curr['state_name']] = []
      accum[curr['state_name']].push(curr['geoid'])
      return accum
    }, {})
  }
}

const mutations = {
  [EnrollmentGroups.Mutations.updateSupplyShed](state, newSupplyShedData) {
    const currentSupplySheds = deepClone(state.supplyShedData)
    for (const newSupplyShed of newSupplyShedData) {
      const toSplice = currentSupplySheds.findIndex(({ id }) => id == newSupplyShed['id'])
      if (toSplice != -1) currentSupplySheds.splice(toSplice, 1, newSupplyShed)
    }

    state.supplyShedData = Object.freeze(currentSupplySheds)
  },
  [EnrollmentGroups.Mutations.setIndividualFilter](state, { key, newValue }) {
    state.filters[key] = newValue
  },
  [EnrollmentGroups.Mutations.setEnrollmentFilters](state, data) {
    state.filters = data
  },
  [EnrollmentGroups.Mutations.setGeoIDLUT](state, data) {
    state.geoIDLUT = data
  },
  [EnrollmentGroups.Mutations.setClientProfiles](state, profiles) {
    state.allClientProfiles = Object.freeze(profiles)
  },
  [EnrollmentGroups.Mutations.setView](state, view) {
    state.view = view
  },
  [EnrollmentGroups.Mutations.setChannelPartnerWideFSS](state, fss) {
    state.channelPartnerWideFSS = Object.freeze(fss)
  },
  [EnrollmentGroups.Mutations.setSupplyShedData](state, supplyShedData) {
    state.supplyShedData = supplyShedData
  },
  [EnrollmentGroups.Mutations.setAllGroupData](state, groupData) {
    state.groupData = Object.freeze(groupData)
  },
  [EnrollmentGroups.Mutations.setIndividualGroupData](state, groupData) {
    state.groupData = Object.freeze({ ...state.groupData, ...groupData })
  },
  [EnrollmentGroups.Mutations.deleteGroupData](state, groupId) {
    const newGroupDataState = deepClone(state.groupData)
    delete newGroupDataState[groupId]
    state.groupData = Object.freeze(newGroupDataState)
  },
  [EnrollmentGroups.Mutations.setFssLoading](state, { fssIds, loading }) {
    const newLoading = [...state[FSS_INDIVIUAL]]

    if (loading) {
      for (const fssId of fssIds)
        if (!newLoading.includes(fssId)) newLoading.push(fssId)
    } else {
      for (const fssId of fssIds)
        if (newLoading.includes(fssId))
          newLoading.splice(newLoading.indexOf(fssId), 1)
    }

    state[FSS_INDIVIUAL] = Object.freeze(newLoading)
  },
  [EnrollmentGroups.Mutations.setTableLoadingState](state, { target, loading, error = null }) {
    state[target] = { loading, error }
  },
}

const actions = {
  [EnrollmentGroups.Actions.fetchChannelPartnerWideFSS]({ commit, rootState }) {
    return new Promise((resolve, reject) => {
      const { year, organization } = rootState.Organization
      if (year == null || organization == null || !("id" in organization)) {
        reject(`Organization or year is null: ${organization}, ${year}`)
        return
      }

      commit(EnrollmentGroups.Mutations.setTableLoadingState, { target: CP_FSS_TABLE, loading: true })
      listFieldsForEntityManagement({ org_node_id: organization['id'], year })
        .then(({ data }) => {
          commit(EnrollmentGroups.Mutations.setChannelPartnerWideFSS, data)
          commit(EnrollmentGroups.Mutations.setTableLoadingState, { target: CP_FSS_TABLE, loading: false })
          resolve()
        })
        .catch(error => {
          commit(EnrollmentGroups.Mutations.setTableLoadingState, { target: CP_FSS_TABLE, loading: false, error })
          reject(error)
        })
    })
  },
  [EnrollmentGroups.Actions.listOverview]({ commit, rootState, state }) {
    return new Promise((resolve, reject) => {
      const { year, organization } = rootState.Organization
      if (year == null || organization == null || !("id" in organization)) {
        reject(`Organization or year is null: ${organization}, ${year}`)
        return
      }

      commit(EnrollmentGroups.Mutations.setTableLoadingState, { target: ENROLLMENT_TABLE, loading: true })
      listOverview({ org_node_id: organization.id, year })
        .then(({ data }) => {
          const { processedGroupData, supplySheds } = processGroupData(state.supplyShedData, data)
          commit(EnrollmentGroups.Mutations.setAllGroupData, processedGroupData)
          commit(EnrollmentGroups.Mutations.setSupplyShedData, supplySheds);
          commit(EnrollmentGroups.Mutations.setTableLoadingState, { target: ENROLLMENT_TABLE, loading: false })
          resolve()
        })
        .catch(e => {
          // if 500 error, just paste the error on the page for the user
          let error = e

          if (('status' in e && e['status'] == 404) || ('response' in e && 'status' in e['response'] && e['response']['status']) == 404) {
            error = `
              No legal entity data was found. To view enrollment groups, pooling allotments must be defined, 
              and legal entities must be created. You may create legal entities via the 'Create New Legal Entity' button
              in the top right. Pools are defined by Arva internally, please reach out to your CSM if you need to define pooling allotments.
            `
          }

          console.log("Error fetching group data overview: ", error)
          commit(EnrollmentGroups.Mutations.setTableLoadingState, { target: ENROLLMENT_TABLE, loading: false, error })

          reject(error)
        })
    })
  },
  [EnrollmentGroups.Actions.fetchSupplySheds]({ commit, rootState, dispatch }, { fetchOverview }) {
    return new Promise((resolve, reject) => {
      const { year, organization } = rootState.Organization
      if (year == null || organization == null || !("id" in organization)) {
        reject(`Organization or year is null: ${organization}, ${year}`)
        return
      }

      commit(EnrollmentGroups.Mutations.setTableLoadingState, { target: SUPPLY_SHED_TABLE, loading: true })
      getSupplySheds({ org_node_id: organization.id, year })
        .then(({ data }) => {
          commit(EnrollmentGroups.Mutations.setSupplyShedData, formatSupplySheds(data))
          commit(EnrollmentGroups.Mutations.setTableLoadingState, { target: SUPPLY_SHED_TABLE, loading: false })

          if (!fetchOverview) resolve()
          else dispatch(EnrollmentGroups.Actions.listOverview).then(() => resolve())
        })
        .catch(error => {
          console.log("Error fetching program supply sheds: ", error)
          commit(EnrollmentGroups.Mutations.setTableLoadingState, { target: SUPPLY_SHED_TABLE, loading: false, error })
          reject(error)
        })
    })
  },
  [EnrollmentGroups.Actions.fetchGeoIDLUT]({ commit, rootState }) {
    return new Promise((resolve, reject) => {
      const { year, organization } = rootState.Organization
      if (year == null || organization == null || !("id" in organization)) {
        reject(`Organization or year is null: ${organization}, ${year}`)
        return
      }

      CarbonReadyAPI.getGeoIDLUT()
        .then(({ data }) => {
          commit(EnrollmentGroups.Mutations.setGeoIDLUT, data)
          resolve()
        })
        .catch(error => {
          console.log("Error fetching geoidlut: ", error)
          reject(error)
        })
    })
  },
  [EnrollmentGroups.Actions.applyFssStatusChangePending]({ commit, state }, fssIds) {
    commit(EnrollmentGroups.Mutations.setFssLoading, { fssIds, loading: true })

    return new Promise((resolve, reject) => {
      applyFssStatusChangePending({ fss_ids: fssIds })
        .then(({ data }) => {
          const { processedGroupData, supplySheds } = processGroupData(state.supplyShedData, data)
          commit(EnrollmentGroups.Mutations.setIndividualGroupData, processedGroupData)
          commit(EnrollmentGroups.Mutations.setSupplyShedData, supplySheds);
          commit(EnrollmentGroups.Mutations.setFssLoading, { fssIds, loading: false })
          resolve()
        })
        .catch(error => {
          reject(error)
        })
    })
  },
  [EnrollmentGroups.Actions.submitSupplyChanges]({ commit, state, rootState }, { group_id, fss_ids }) {
    commit(EnrollmentGroups.Mutations.setTableLoadingState, { target: ENROLLMENT_TABLE, loading: true })

    return new Promise((resolve, reject) => {
      const { year } = rootState.Organization
      if (year == null) {
        reject(`Year is null: ${year}`)
        commit(EnrollmentGroups.Mutations.setTableLoadingState, { target: ENROLLMENT_TABLE, loading: false, error: 'Year is null' })
        return
      }

      // combine the fss ids with their proposed supply sheds
      const fss_supply_shed_ids = state.groupData[group_id]['fieldsupply']
        .filter(({ id }) => fss_ids.includes(id))
        .map(({ id, proposed_supply_shed_id }) => [id, proposed_supply_shed_id])

      submitSupplyRequests({ year, group_id, fss_supply_shed_ids })
        .then(({ data: { group_data, rollup_data } }) => {
          // update supply shed data, we get requested/enrolled numbers from server side
          commit(EnrollmentGroups.Mutations.updateSupplyShed, formatSupplySheds(rollup_data))

          // update the usual flow using the new supply shed data
          const { processedGroupData, supplySheds } = processGroupData(state.supplyShedData, group_data)
          commit(EnrollmentGroups.Mutations.setIndividualGroupData, processedGroupData)
          commit(EnrollmentGroups.Mutations.setSupplyShedData, supplySheds);
          commit(EnrollmentGroups.Mutations.setTableLoadingState, { target: ENROLLMENT_TABLE, loading: false })
          resolve()
        })
        .catch(error => {
          console.log("Error fetching group data after supply submission", error)
          commit(EnrollmentGroups.Mutations.setTableLoadingState, { target: ENROLLMENT_TABLE, loading: false, error })
          reject(error)
        })
    })
  },
  [EnrollmentGroups.Actions.submitEvidencingLink]({ commit, state }, { group_id, updated_link_obj }) {
    commit(EnrollmentGroups.Mutations.setTableLoadingState, {
      target: ENROLLMENT_TABLE,
      loading: true,
    })
    submitEvidencingLink({ group_id, updated_link_obj })
      .then(() => {
        const newGroupData = {}
        newGroupData[group_id] = {
          ...state.groupData[group_id],
          evidencing_links: updated_link_obj,
        }
        commit(EnrollmentGroups.Mutations.setIndividualGroupData, newGroupData)
        commit(EnrollmentGroups.Mutations.setTableLoadingState, {
          target: ENROLLMENT_TABLE,
          loading: false,
        })
      })
      .catch(error => {
        commit(EnrollmentGroups.Mutations.setTableLoadingState, {
          target: ENROLLMENT_TABLE,
          loading: false,
          error,
        })
      })
  },
  [EnrollmentGroups.Actions.confirmSupplyChanges]({ commit, state, rootState }, payload) {
    commit(EnrollmentGroups.Mutations.setTableLoadingState, { target: ENROLLMENT_TABLE, loading: true })

    return new Promise((resolve, reject) => {
      const { year } = rootState.Organization
      if (year == null) {
        reject(`Year is null: ${year}`)
        commit(EnrollmentGroups.Mutations.setTableLoadingState, { target: ENROLLMENT_TABLE, loading: false, error: 'Year is null' })
        return
      }

      submitConfirmRequests({ year, ...payload })
        .then(({ data: { group_data, rollup_data } }) => {
          // update supply shed data, we get requested/enrolled numbers from server side
          commit(EnrollmentGroups.Mutations.updateSupplyShed, formatSupplySheds(rollup_data))

          // update the usual flow using the new supply shed data
          const { processedGroupData, supplySheds } = processGroupData(state.supplyShedData, group_data)
          commit(EnrollmentGroups.Mutations.setIndividualGroupData, processedGroupData)
          commit(EnrollmentGroups.Mutations.setSupplyShedData, supplySheds);
          commit(EnrollmentGroups.Mutations.setTableLoadingState, { target: ENROLLMENT_TABLE, loading: false })
          resolve()
        })
        .catch(error => {
          console.log("Error fetching group data after supply confirm", error)
          commit(EnrollmentGroups.Mutations.setTableLoadingState, { target: ENROLLMENT_TABLE, loading: false, error })
          reject(error)
        })
    })
  },
  [EnrollmentGroups.Actions.updateLegalEntityMetadata]({ commit, state }, { group_id, name, notes }) {
    commit(EnrollmentGroups.Mutations.setTableLoadingState, { target: ENROLLMENT_TABLE, loading: true })

    return new Promise((resolve, reject) => {
      updateLegalEntityMetadata({ group_id, name, notes })
        .then(({ data }) => {
          const { processedGroupData, supplySheds } = processGroupData(state.supplyShedData, data)
          commit(EnrollmentGroups.Mutations.setIndividualGroupData, processedGroupData)
          commit(EnrollmentGroups.Mutations.setSupplyShedData, supplySheds);
          commit(EnrollmentGroups.Mutations.setTableLoadingState, { target: ENROLLMENT_TABLE, loading: false })
          resolve()
        })
        .catch(error => {
          console.log("Error updating legal entity metadata", error)
          commit(EnrollmentGroups.Mutations.setTableLoadingState, { target: ENROLLMENT_TABLE, loading: false, error })
          reject(error)
        })
    })
  },
  [EnrollmentGroups.Actions.removeFieldsFromLegalEntity]({ commit, state }, { group_id, fss_ids }) {
    commit(EnrollmentGroups.Mutations.setTableLoadingState, { target: ENROLLMENT_TABLE, loading: true })

    return new Promise((resolve, reject) => {
      removeFieldsFromLegalEntity({ group_id, fss_ids })
        .then(({ data, status }) => {
          if (status == 403) {
            // we just attempted to remove fss from a legal entity that is under contract
            // render an error to the user
            const error = 'Error: failed to remove field due to entity under contract!'
            commit(EnrollmentGroups.Mutations.setTableLoadingState, { target: ENROLLMENT_TABLE, loading: false, error })
            reject(error)
            return
          }

          const { processedGroupData, supplySheds } = processGroupData(state.supplyShedData, data)
          commit(EnrollmentGroups.Mutations.setIndividualGroupData, processedGroupData)
          commit(EnrollmentGroups.Mutations.setSupplyShedData, supplySheds);
          commit(EnrollmentGroups.Mutations.setTableLoadingState, { target: ENROLLMENT_TABLE, loading: false })
          resolve()
        })
        .catch(error => {
          console.log("Error removing fss from entity", error)
          commit(EnrollmentGroups.Mutations.setTableLoadingState, { target: ENROLLMENT_TABLE, loading: false, error })
          reject(error)
        })
    })
  },
  [EnrollmentGroups.Actions.addFieldsToLegalEntity]({ commit, state }, { group_id, fss_ids }) {
    commit(EnrollmentGroups.Mutations.setTableLoadingState, { target: ENROLLMENT_TABLE, loading: true })

    return new Promise((resolve, reject) => {
      addFieldsToLegalEntity({ group_id, fss_ids })
        .then(({ data, status }) => {
          if (status == 403) {
            // we just attempted to remove fss from a legal entity that is under contract
            // render an error to the user
            const error = 'Error: failed to remove field due to entity under contract!'
            commit(EnrollmentGroups.Mutations.setTableLoadingState, { target: ENROLLMENT_TABLE, loading: false, error })
            reject(error)
            return
          }

          const { processedGroupData, supplySheds } = processGroupData(state.supplyShedData, data)
          commit(EnrollmentGroups.Mutations.setIndividualGroupData, processedGroupData)
          commit(EnrollmentGroups.Mutations.setSupplyShedData, supplySheds)
          commit(EnrollmentGroups.Mutations.setTableLoadingState, { target: ENROLLMENT_TABLE, loading: false })
          resolve()
        })
        .catch(error => {
          console.log("Error adding fss to entity", error)
          commit(EnrollmentGroups.Mutations.setTableLoadingState, { target: ENROLLMENT_TABLE, loading: false, error })
          reject(error)
        })
    })
  },
  [EnrollmentGroups.Actions.createNewLegalEntity]({ commit, rootState, state }, { name, notes, w9_file, fss_ids }) {
    return new Promise((resolve, reject) => {
      commit(EnrollmentGroups.Mutations.setTableLoadingState, { target: ENROLLMENT_TABLE, loading: true })

      const { year, organization } = rootState.Organization
      if (year == null || organization == null || !("id" in organization)) {
        reject(`Organization or year is null: ${organization}, ${year}`)
        return
      }

      const formData = new FormData()
      formData.append("name", name)
      formData.append("year", year)
      formData.append("notes", notes)
      formData.append("raw_data", w9_file)
      formData.append("fss_ids", fss_ids)
      formData.append("organization_node_id", organization['id'])

      createNewLegalEntity(formData)
        .then(({ data }) => {
          const { processedGroupData, supplySheds } = processGroupData(state.supplyShedData, data)
          commit(EnrollmentGroups.Mutations.setIndividualGroupData, processedGroupData)
          commit(EnrollmentGroups.Mutations.setSupplyShedData, supplySheds);
          commit(EnrollmentGroups.Mutations.setTableLoadingState, { target: ENROLLMENT_TABLE, loading: false })
          resolve()
        })
        .catch(error => {
          console.log("Error fetching creating new legal entity: ", error)
          commit(EnrollmentGroups.Mutations.setTableLoadingState, { target: ENROLLMENT_TABLE, loading: false, error })
          reject(error)
        })
    })
  },
  [EnrollmentGroups.Actions.deleteLegalEntity]({ commit }, { group_id }) {
    return new Promise((resolve, reject) => {
      commit(EnrollmentGroups.Mutations.setTableLoadingState, { target: ENROLLMENT_TABLE, loading: true })

      deleteLegalEntity({ group_id })
        .then(({ data }) => {
          commit(EnrollmentGroups.Mutations.deleteGroupData, data)
          commit(EnrollmentGroups.Mutations.setTableLoadingState, { target: ENROLLMENT_TABLE, loading: false })
          resolve()
        })
        .catch(error => {
          console.log("Error deleting legal entity: ", error)
          commit(EnrollmentGroups.Mutations.setTableLoadingState, { target: ENROLLMENT_TABLE, loading: false, error })
          reject(error)
        })
    })
  },
  [EnrollmentGroups.Actions.fetchClientProfiles]({ rootState, commit }) {
    return new Promise((resolve, reject) => {
      const { year, organization } = rootState.Organization
      if (year == null || organization == null || !("id" in organization)) {
        reject(`Organization or year is null: ${organization}, ${year}`)
        return
      }
      if (state.allClientProfiles != null) resolve()
      else {
        commit(EnrollmentGroups.Mutations.setTableLoadingState, { target: CLIENT_PROFILES, loading: true })
        fetchClientProfiles()
          .then(({ data }) => {
            const filteredData = data.filter(({ poc_email, first_name, last_name }) =>
              poc_email != null || (first_name != null && last_name != null)
            )

            commit(EnrollmentGroups.Mutations.setClientProfiles, filteredData)
            commit(EnrollmentGroups.Mutations.setTableLoadingState, { target: CLIENT_PROFILES, loading: false })
            resolve()
            return
          })
          .catch(error => {
            console.log("Error fetching client profiles: ", error)
            commit(EnrollmentGroups.Mutations.setTableLoadingState, { target: CLIENT_PROFILES, loading: false, error })
            reject(error)
          })
      }
    })
  },
  [EnrollmentGroups.Actions.fetchGroupClientProfiles]({ commit, state }, { group_id }) {
    return new Promise((resolve, reject) => {
      commit(EnrollmentGroups.Mutations.setTableLoadingState, { target: CLIENT_PROFILES, loading: true })
      fetchGroupClientProfiles({ group_id })
        .then(({ data }) => {
          const toParse = {}
          toParse[group_id] = state.groupData[group_id]
          toParse[group_id]["cp_profiles"] = data.filter(({ profile_type }) => profile_type == "cp")
          toParse[group_id]["grower_profiles"] = data.filter(({ profile_type }) => profile_type == "grower")
          toParse[group_id]["landowner_profiles"] = data.filter(({ profile_type }) => profile_type == "landowner")

          const { processedGroupData, supplySheds } = processGroupData(state.supplyShedData, toParse)
          commit(EnrollmentGroups.Mutations.setIndividualGroupData, processedGroupData)
          commit(EnrollmentGroups.Mutations.setSupplyShedData, supplySheds);
          commit(EnrollmentGroups.Mutations.setTableLoadingState, { target: CLIENT_PROFILES, loading: false })
          resolve()
        })
        .catch(error => {
          console.log("Error fetching group client profiles: ", error)
          commit(EnrollmentGroups.Mutations.setTableLoadingState, { target: CLIENT_PROFILES, loading: false, error })
          reject(error)
        })
    })
  },

  [EnrollmentGroups.Actions.editClientProfiles]({ commit, state }, { type, payload }) {
    let api = null

    if (type == ADD_PROFILE) api = addProfileToGroup
    if (type == REMOVE_PROFILE) api = removeProfileFromGroup
    if (type == UPDATE_PROFILE) api = updateProfile
    if (type == SWAP_PROFILE) api = swapGroupProfile
    if (type == CREATE_PROFILE) api = createProfile

    commit(EnrollmentGroups.Mutations.setTableLoadingState, { target: CLIENT_PROFILES, loading: true })
    return api(payload)
      .then(({ data }) => {
        const { processedGroupData, supplySheds } = processGroupData(state.supplyShedData, data)
        commit(EnrollmentGroups.Mutations.setIndividualGroupData, processedGroupData)
        commit(EnrollmentGroups.Mutations.setSupplyShedData, supplySheds)
        commit(EnrollmentGroups.Mutations.setTableLoadingState, { target: CLIENT_PROFILES, loading: false })
        return processedGroupData
      })
      .catch(error => {
        console.log("error editing client profiles", error)
        commit(EnrollmentGroups.Mutations.setTableLoadingState, { target: CLIENT_PROFILES, loading: false, error })
      })
  },
  [EnrollmentGroups.Actions.submitContractGeneration]({ commit, state }, { groupId, landOwnership }) {
    return new Promise((resolve, reject) => {
      commit(EnrollmentGroups.Mutations.setTableLoadingState, { target: ENROLLMENT_TABLE, loading: true })
      commit(EnrollmentGroups.Mutations.setTableLoadingState, { target: CLIENT_PROFILES, loading: true })

      CarbonReadyAPI.postGenerateInsetContract({ groupId, landOwnership })
        .then(({ data }) => {
          commit(EnrollmentGroups.Mutations.setTableLoadingState, { target: ENROLLMENT_TABLE, loading: false })
          commit(EnrollmentGroups.Mutations.setTableLoadingState, { target: CLIENT_PROFILES, loading: false })

          const { processedGroupData, supplySheds } = processGroupData(state.supplyShedData, data)
          commit(EnrollmentGroups.Mutations.setIndividualGroupData, processedGroupData)
          commit(EnrollmentGroups.Mutations.setSupplyShedData, supplySheds);
          resolve(data)
        })
        .catch(error => {
          commit(EnrollmentGroups.Mutations.setTableLoadingState, { target: ENROLLMENT_TABLE, loading: false, error })
          commit(EnrollmentGroups.Mutations.setTableLoadingState, { target: CLIENT_PROFILES, loading: false, error })
          reject(error)
        })
    })
  },
  [EnrollmentGroups.Actions.submitVoidContract]({ commit, state }, { groupId, enrollmentId, voidedReason }) {
    return new Promise((resolve, reject) => {
      commit(EnrollmentGroups.Mutations.setTableLoadingState, { target: ENROLLMENT_TABLE, loading: true })
      commit(EnrollmentGroups.Mutations.setTableLoadingState, { target: CLIENT_PROFILES, loading: true })
      CarbonReadyAPI.postVoidContract({ groupId, enrollmentId, voidedReason })
        .then(({ data }) => {
          commit(EnrollmentGroups.Mutations.setTableLoadingState, { target: ENROLLMENT_TABLE, loading: false })
          commit(EnrollmentGroups.Mutations.setTableLoadingState, { target: CLIENT_PROFILES, loading: false })

          const { processedGroupData, supplySheds } = processGroupData(state.supplyShedData, data)
          commit(EnrollmentGroups.Mutations.setIndividualGroupData, processedGroupData)
          commit(EnrollmentGroups.Mutations.setSupplyShedData, supplySheds)
          resolve(data)
        })
        .catch(error => {
          commit(EnrollmentGroups.Mutations.setTableLoadingState, { target: ENROLLMENT_TABLE, loading: false, error })
          commit(EnrollmentGroups.Mutations.setTableLoadingState, { target: CLIENT_PROFILES, loading: false, error })
          reject(error)
        })
    })
  },
  [EnrollmentGroups.Actions.submitContractEsign]({ commit, state }, { groupId, enrollmentId }) {
    return new Promise((resolve, reject) => {
      commit(EnrollmentGroups.Mutations.setTableLoadingState, { target: ENROLLMENT_TABLE, loading: true })
      commit(EnrollmentGroups.Mutations.setTableLoadingState, { target: CLIENT_PROFILES, loading: true })

      CarbonReadyAPI.postESignContract({ groupId, enrollmentId })
        .then(({ data }) => {
          commit(EnrollmentGroups.Mutations.setTableLoadingState, { target: ENROLLMENT_TABLE,loading: false })
          commit(EnrollmentGroups.Mutations.setTableLoadingState, { target: CLIENT_PROFILES, loading: false })

          const { processedGroupData, supplySheds } = processGroupData(state.supplyShedData, data)
          commit(EnrollmentGroups.Mutations.setIndividualGroupData, processedGroupData)
          commit(EnrollmentGroups.Mutations.setSupplyShedData, supplySheds);
          resolve(data)
        })
        .catch(error => {
          commit(EnrollmentGroups.Mutations.setTableLoadingState, { target: ENROLLMENT_TABLE, loading: false, error })
          commit(EnrollmentGroups.Mutations.setTableLoadingState, { target: CLIENT_PROFILES, loading: false, error })
          reject(error)
        })
    })
  },
  [EnrollmentGroups.Actions.submitContractAdditionalEsign]({ commit, state }, { groupId, enrollmentId }) {
    return new Promise((resolve, reject) => {
      commit(EnrollmentGroups.Mutations.setTableLoadingState, { target: ENROLLMENT_TABLE, loading: true })
      commit(EnrollmentGroups.Mutations.setTableLoadingState, { target: CLIENT_PROFILES, loading: true })

      CarbonReadyAPI.postAdditionalESignContract({ groupId, enrollmentId })
        .then(({ data }) => {
          commit(EnrollmentGroups.Mutations.setTableLoadingState, { target: ENROLLMENT_TABLE,loading: false })
          commit(EnrollmentGroups.Mutations.setTableLoadingState, { target: CLIENT_PROFILES,loading: false })

          const { processedGroupData, supplySheds } = processGroupData(state.supplyShedData, data)
          commit(EnrollmentGroups.Mutations.setIndividualGroupData, processedGroupData)
          commit(EnrollmentGroups.Mutations.setSupplyShedData, supplySheds);
          resolve(data)
        })
        .catch(error => {
          commit(EnrollmentGroups.Mutations.setTableLoadingState, { target: ENROLLMENT_TABLE, loading: false, error })
          commit(EnrollmentGroups.Mutations.setTableLoadingState, { target: CLIENT_PROFILES, loading: false, error })
          reject(error)
        })
    })
  },
  [EnrollmentGroups.Actions.submitContractCounterEsign]({ commit, state }, { groupId, enrollmentId }) {
    return new Promise((resolve, reject) => {
      commit(EnrollmentGroups.Mutations.setTableLoadingState, { target: ENROLLMENT_TABLE, loading: true })
      commit(EnrollmentGroups.Mutations.setTableLoadingState, { target: CLIENT_PROFILES, loading: true })

      CarbonReadyAPI.postCounterESignContract({ groupId, enrollmentId })
        .then(({ data }) => {
          commit(EnrollmentGroups.Mutations.setTableLoadingState, { target: ENROLLMENT_TABLE, loading: false })
          commit(EnrollmentGroups.Mutations.setTableLoadingState, { target: CLIENT_PROFILES, loading: false })

          const { processedGroupData, supplySheds } = processGroupData(state.supplyShedData, data)
          commit(EnrollmentGroups.Mutations.setIndividualGroupData, processedGroupData)
          commit(EnrollmentGroups.Mutations.setSupplyShedData, supplySheds);
          resolve(data)
        })
        .catch(error => {
          commit(EnrollmentGroups.Mutations.setTableLoadingState, { target: ENROLLMENT_TABLE, loading: false, error })
          commit(EnrollmentGroups.Mutations.setTableLoadingState, { target: CLIENT_PROFILES, loading: false, error })
          reject(error)
        })
    })
  },
}

export default {
  state,
  mutations,
  getters,
  actions
}
